import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, UserCog, KeyRound, Trash2, Copy, Check, ShieldCheck, Ban, CircleCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SecuritySettingsCard } from "@/components/SecuritySettingsCard";
import { AuditLogPanel } from "@/components/AuditLogPanel";

const EMPTY = { name: "", email: "", role: "manager" };
const TRUST_OPTIONS = [
  { v: 0, label: "Every sign-in" },
  { v: 1, label: "1 day" },
  { v: 7, label: "7 days" },
  { v: 14, label: "14 days" },
  { v: 30, label: "30 days" },
  { v: 90, label: "90 days" },
];

// Dealer-set window for how long a verified device skips the emailed code, per role
function MfaTrustSettings() {
  const [dealer, setDealer] = useState(null);
  useEffect(() => { api.get("/dealer").then(({ data }) => setDealer(data)).catch(() => setDealer(null)); }, []);
  if (!dealer) return null;
  const set = async (key, v) => {
    try {
      const { data } = await api.put("/dealer", { [key]: Number(v) });
      setDealer(data);
      toast.success(Number(v) === 0 ? "A code will be asked on every sign-in" : `Devices trusted for ${v} day${Number(v) === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const Picker = ({ role }) => (
    <div className="flex items-center gap-3">
      <span className="text-sm text-slate-300 w-20 capitalize">{role}s</span>
      <Select value={String(dealer[`mfa_trust_days_${role}`] ?? 30)} onValueChange={(v) => set(`mfa_trust_days_${role}`, v)}>
        <SelectTrigger data-testid={`mfa-trust-${role}`} className="w-40 bg-slate-950 border-slate-800 text-slate-200"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#12141C] border-[#2A2E3D] text-slate-200">
          {TRUST_OPTIONS.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
  return (
    <div className="rounded-xl border border-[#2A2E3D] bg-[#12141C] px-5 py-4 mb-4 flex flex-col md:flex-row md:items-center gap-4" data-testid="mfa-trust-settings">
      <div className="flex-1 min-w-0">
        <div className="font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#0A8CF2]" /> Trusted devices</div>
        <p className="text-xs text-slate-400 mt-0.5">After a code is verified, how long that device is trusted before asking again. Applies to users with MFA on.</p>
      </div>
      <Picker role="admin" />
      <Picker role="manager" />
    </div>
  );
}

function CredentialDialog({ cred, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(`Email: ${cred.email}\nTemporary password: ${cred.temp_password}`);
    setCopied(true);
  };
  return (
    <Dialog open={!!cred} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F111A] border-[#2A2E3D] text-white max-w-md" data-testid="credential-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#0A8CF2]" /> {cred?.title}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Share these with the team member privately. The temporary password is shown only once — they can change it from the dashboard.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-slate-800 bg-slate-950 p-4 space-y-2 font-mono-tech text-sm">
          <div><span className="text-slate-500">Email </span><span data-testid="credential-email">{cred?.email}</span></div>
          <div><span className="text-slate-500">Temp password </span><span data-testid="credential-password" className="text-[#7FC3FF] font-bold">{cred?.temp_password}</span></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={copy} data-testid="copy-credentials-btn" className="border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200">
            {copied ? <Check className="mr-2 h-4 w-4 text-emerald-400" /> : <Copy className="mr-2 h-4 w-4" />}
            {copied ? "Copied" : "Copy"}
          </Button>
          <Button onClick={onClose} data-testid="credential-done-btn" className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Team() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [cred, setCred] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/team").then(({ data }) => setList(data)).catch(() => toast.error("Failed to load team")).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const run = async (fn, ok) => {
    try {
      await fn();
      if (ok) toast.success(ok);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/team", form);
      setOpen(false);
      setForm(EMPTY);
      setCred({ email: data.email, temp_password: data.temp_password, title: "Login created" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const resetPw = (u) =>
    run(async () => {
      const { data } = await api.post(`/team/${u.id}/reset-password`);
      setCred({ email: u.email, temp_password: data.temp_password, title: "Password reset" });
    });
  const toggleActive = (u) => run(() => api.put(`/team/${u.id}`, { active: !u.active }), u.active ? "Login disabled" : "Login enabled");
  const toggleMfa = (u) => run(() => api.put(`/team/${u.id}`, { mfa_enabled: !u.mfa_enabled }), u.mfa_enabled ? "Email code turned off" : "Email sign-in code now required");
  const setRole = (u, role) => run(() => api.put(`/team/${u.id}`, { role }), "Role updated");
  const remove = (u) => window.confirm(`Remove ${u.name}'s login?`) && run(() => api.delete(`/team/${u.id}`), "Login removed");

  if (!isAdmin) {
    return <p className="text-slate-400" data-testid="team-admin-only">Only dealership admins can manage team logins.</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Team logins</h1>
          <p className="text-sm text-slate-400 mt-1">Dealer admins and managers who can access this dashboard. <b className="text-slate-300">MFA</b> emails a 6-digit sign-in code; Vinconex platform accounts are asked on every sign-in.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-team-member-btn" className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold shadow-lg shadow-blue-900/40">
          <Plus className="mr-2 h-4 w-4" /> Add login
        </Button>
      </div>

      <MfaTrustSettings />
      <SecuritySettingsCard />

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#0A8CF2]" /></div>
      ) : (
        <div className="rounded-xl border border-[#2A2E3D] bg-[#12141C] divide-y divide-[#2A2E3D]" data-testid="team-list">
          {list.map((u) => {
            const self = u.id === user.id;
            return (
              <div key={u.id} data-testid={`team-member-${u.id}`} className="flex flex-col md:flex-row md:items-center gap-3 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate">{u.name}</span>
                    {self && <span className="text-[10px] text-slate-500 uppercase tracking-widest font-mono-tech">you</span>}
                    {!u.active && <Badge variant="outline" className="border-red-800/50 bg-red-950/40 text-red-400" data-testid={`team-disabled-${u.id}`}>disabled</Badge>}
                    {u.mfa_enabled && (
                      <Badge variant="outline" data-testid={`team-mfa-badge-${u.id}`} title={u.mfa_required ? "Always required for Vinconex platform access (every sign-in)" : "Email sign-in code"} className="border-emerald-800/50 bg-emerald-950/40 text-emerald-400 text-[10px]">
                        MFA{u.mfa_required ? " · every sign-in" : ""}
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-slate-400 truncate">{u.email}</div>
                </div>
                <Select value={u.role} onValueChange={(v) => setRole(u, v)} disabled={self}>
                  <SelectTrigger data-testid={`team-role-${u.id}`} className="w-36 bg-slate-950 border-slate-800 text-slate-200">
                    <ShieldCheck className="h-3.5 w-3.5 text-[#0A8CF2] mr-1.5" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12141C] border-[#2A2E3D] text-slate-200">
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => toggleMfa(u)} disabled={u.mfa_required} data-testid={`team-mfa-${u.id}`} title={u.mfa_required ? "MFA is always on for platform access" : u.mfa_enabled ? "Turn off the email sign-in code" : "Require an emailed sign-in code"} className={`h-9 ${u.mfa_enabled ? "border-emerald-700/60 bg-emerald-950/40 hover:bg-emerald-950/70 text-emerald-300" : "border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"}`}>
                    <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> {u.mfa_enabled ? "MFA on" : "MFA off"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => resetPw(u)} data-testid={`team-reset-${u.id}`} className="h-9 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200">
                    <KeyRound className="mr-1.5 h-3.5 w-3.5" /> Reset password
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => toggleActive(u)} disabled={self} data-testid={`team-toggle-${u.id}`} className="h-9 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200">
                    {u.active ? <Ban className="h-3.5 w-3.5" /> : <CircleCheck className="h-3.5 w-3.5 text-emerald-400" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => remove(u)} disabled={self} data-testid={`team-remove-${u.id}`} className="h-9 border-red-900/50 bg-red-950/30 hover:bg-red-950/60 text-red-400">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6">
        <AuditLogPanel />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F111A] border-[#2A2E3D] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-extrabold flex items-center gap-2">
              <UserCog className="h-5 w-5 text-[#0A8CF2]" /> New dashboard login
            </DialogTitle>
            <DialogDescription className="text-slate-400">A temporary password is generated and shown once after you save. New passwords must be 12+ characters and are checked against known breaches.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-300 mb-1 block">Full name</Label>
              <Input data-testid="team-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
            </div>
            <div>
              <Label className="text-slate-300 mb-1 block">Work email</Label>
              <Input type="email" data-testid="team-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
            </div>
            <div>
              <Label className="text-slate-300 mb-1 block">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="team-role-select" className="bg-slate-950 border-slate-800 text-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#12141C] border-[#2A2E3D] text-slate-200">
                  <SelectItem value="manager">Manager — inventory, QR codes, leads</SelectItem>
                  <SelectItem value="admin">Admin — full control incl. settings & team</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={create} disabled={saving || !form.name || !form.email} data-testid="save-team-member-btn" className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create login"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CredentialDialog cred={cred} onClose={() => setCred(null)} />
    </div>
  );
}
